import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Common/button';
import { useIsSuperAdmin } from '@/stores/authStore';
import { notify } from '@/lib/notify';
import configurationService from '@/services/configurationService';
import type {
  LLMProvider,
  LLMProviderTypeDescriptor,
} from '@/services/configurationService';

type LLMProvidersCardProps = {
  organizationId: string | null | undefined;
  /** Lets the parent refresh its per-service pickers after a change. */
  onProvidersChanged?: (providers: LLMProvider[]) => void;
};

type DraftProvider = {
  id: string | null;
  provider_type: string;
  label: string;
  base_url: string;
  api_key: string;
  api_version: string;
  deployment_map: string;
  is_active: boolean;
};

const EMPTY_DRAFT: DraftProvider = {
  id: null,
  provider_type: '',
  label: '',
  base_url: '',
  api_key: '',
  api_version: '',
  deployment_map: '',
  is_active: true,
};

const inputClass =
  'w-full rounded-md border border-border bg-background px-2 py-1 text-sm';

/**
 * Org-managed AI provider connections, shown in the "Service IA" tab.
 *
 * Admins declare a provider once (type, endpoint, credentials) and then bind
 * individual AI services to it from the grid above. API keys are write-only:
 * the backend only ever returns presence plus the last four characters.
 */
export function LLMProvidersCard({ organizationId, onProvidersChanged }: LLMProvidersCardProps) {
  const { t } = useTranslation();
  // Only super-admins may leave a provider without its own key, which makes it
  // fall back to the platform's shared server credential.
  const isSuperAdmin = useIsSuperAdmin();
  const [providers, setProviders] = useState<LLMProvider[]>([]);
  const [providerTypes, setProviderTypes] = useState<LLMProviderTypeDescriptor[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftProvider | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const typesByName = useMemo(
    () => new Map(providerTypes.map((type) => [type.provider_type, type])),
    [providerTypes]
  );

  const selectedType = draft ? typesByName.get(draft.provider_type) : undefined;
  // Databricks Model Serving uses the app's own workspace identity: no key needed.
  const keyRequired = !!draft && !draft.id && !isSuperAdmin && !selectedType?.uses_workspace_identity;
  const needsAzureFields = draft?.provider_type === 'azure_openai';

  const publish = useCallback(
    (next: LLMProvider[]) => {
      setProviders(next);
      onProvidersChanged?.(next);
    },
    [onProvidersChanged]
  );

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;

    setLoading(true);
    setLoadError(null);

    // Settled independently on purpose: the provider list and the static type
    // catalog are unrelated calls. Loading them with Promise.all meant one
    // failure left the other unset, which showed up as an empty "Provider type"
    // dropdown with no explanation.
    void Promise.allSettled([
      configurationService.getLLMProviders(organizationId),
      configurationService.getLLMProviderTypes(),
    ])
      .then(([providerResult, typeResult]) => {
        if (cancelled) return;

        if (providerResult.status === 'fulfilled') {
          publish(providerResult.value.providers ?? []);
        } else {
          console.error('Failed to load AI providers:', providerResult.reason);
        }

        if (typeResult.status === 'fulfilled') {
          setProviderTypes(typeResult.value.provider_types ?? []);
        } else {
          console.error('Failed to load AI provider types:', typeResult.reason);
        }

        const failure =
          providerResult.status === 'rejected'
            ? providerResult.reason
            : typeResult.status === 'rejected'
              ? typeResult.reason
              : null;
        if (failure) {
          setLoadError(
            resolveApiError(
              failure,
              t(
                'knowledgeManagement.llmProviders.loadError',
                'Impossible de charger les fournisseurs IA.'
              )
            )
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId, publish, t]);

  const startCreate = useCallback(() => {
    setDraft({ ...EMPTY_DRAFT, provider_type: providerTypes[0]?.provider_type ?? '' });
  }, [providerTypes]);

  const startEdit = useCallback((provider: LLMProvider) => {
    const extra = provider.extra_config ?? {};
    const deploymentMap = extra.deployment_map;
    setDraft({
      id: provider.id,
      provider_type: provider.provider_type,
      label: provider.label,
      base_url: provider.base_url ?? '',
      // Never prefilled — the backend does not return stored keys.
      api_key: '',
      api_version: typeof extra.api_version === 'string' ? extra.api_version : '',
      deployment_map: deploymentMap ? JSON.stringify(deploymentMap, null, 2) : '',
      is_active: provider.is_active,
    });
  }, []);

  const buildExtraConfig = useCallback((current: DraftProvider): Record<string, unknown> | null => {
    if (current.provider_type !== 'azure_openai') return {};
    const extra: Record<string, unknown> = {};
    if (current.api_version.trim()) extra.api_version = current.api_version.trim();
    if (current.deployment_map.trim()) {
      try {
        const parsed: unknown = JSON.parse(current.deployment_map);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          throw new Error('not an object');
        }
        extra.deployment_map = parsed;
      } catch {
        notify.error(
          t('knowledgeManagement.llmProviders.invalidJsonTitle', 'JSON invalide'),
          t(
            'knowledgeManagement.llmProviders.invalidDeploymentMap',
            'La correspondance des déploiements doit être un objet JSON, par ex. {"gpt-4o": "mon-deploiement"}.'
          )
        );
        return null;
      }
    }
    return extra;
  }, [t]);

  const handleSave = useCallback(async () => {
    if (!organizationId || !draft) return;
    if (!draft.label.trim()) {
      notify.error(
        t('knowledgeManagement.llmProviders.missingLabelTitle', 'Nom manquant'),
        t('knowledgeManagement.llmProviders.missingLabel', 'Donnez un nom à ce fournisseur.')
      );
      return;
    }

    // Creating without a key means "use the platform's shared server key",
    // which is a super-admin-only decision. On edit an empty field just keeps
    // the stored key, so it is always allowed.
    if (keyRequired && !draft.api_key.trim()) {
      notify.error(
        t('knowledgeManagement.llmProviders.missingApiKeyTitle', 'Clé API manquante'),
        t(
          'knowledgeManagement.llmProviders.missingApiKey',
          'Renseignez la clé API de votre organisation pour ce fournisseur.'
        )
      );
      return;
    }

    const extraConfig = buildExtraConfig(draft);
    if (extraConfig === null) return;

    setSaving(true);
    try {
      if (draft.id) {
        await configurationService.updateLLMProvider(organizationId, draft.id, {
          label: draft.label.trim(),
          base_url: draft.base_url.trim() || null,
          // Only send a key when the admin typed a new one, so saving other
          // fields never wipes the stored credential.
          ...(draft.api_key.trim() ? { api_key: draft.api_key.trim() } : {}),
          extra_config: extraConfig,
          is_active: draft.is_active,
        });
      } else {
        await configurationService.createLLMProvider(organizationId, {
          provider_type: draft.provider_type,
          label: draft.label.trim(),
          base_url: draft.base_url.trim() || null,
          api_key: draft.api_key.trim() || undefined,
          extra_config: extraConfig,
          is_active: draft.is_active,
        });
      }

      const refreshed = await configurationService.getLLMProviders(organizationId);
      publish(refreshed.providers ?? []);
      setDraft(null);
      notify.success(
        t('knowledgeManagement.llmProviders.savedTitle', 'Fournisseur enregistré'),
        t('knowledgeManagement.llmProviders.savedMessage', 'La configuration a été mise à jour.')
      );
    } catch (error) {
      notify.error(
        t('knowledgeManagement.llmProviders.saveErrorTitle', 'Erreur'),
        resolveApiError(
          error,
          t('knowledgeManagement.llmProviders.saveError', "Impossible d'enregistrer le fournisseur.")
        )
      );
    } finally {
      setSaving(false);
    }
  }, [buildExtraConfig, draft, keyRequired, organizationId, publish, t]);

  const handleDelete = useCallback(
    async (provider: LLMProvider) => {
      if (!organizationId) return;
      const confirmed = window.confirm(
        t('knowledgeManagement.llmProviders.confirmDelete', {
          defaultValue: 'Supprimer le fournisseur « {{label}} » ?',
          label: provider.label,
        })
      );
      if (!confirmed) return;

      try {
        await configurationService.deleteLLMProvider(organizationId, provider.id);
        const refreshed = await configurationService.getLLMProviders(organizationId);
        publish(refreshed.providers ?? []);
      } catch (error) {
        // A 409 here means services are still bound to it; the backend names them.
        notify.error(
          t('knowledgeManagement.llmProviders.deleteErrorTitle', 'Suppression impossible'),
          resolveApiError(
            error,
            t('knowledgeManagement.llmProviders.deleteError', 'Impossible de supprimer le fournisseur.')
          )
        );
      }
    },
    [organizationId, publish, t]
  );

  const handleTest = useCallback(
    async (provider: LLMProvider) => {
      if (!organizationId) return;
      setTestingId(provider.id);
      try {
        const result = await configurationService.testLLMProvider(organizationId, provider.id);
        if (result.ok) {
          notify.success(
            t('knowledgeManagement.llmProviders.testOkTitle', 'Connexion réussie'),
            result.detail ?? `${result.latency_ms ?? 0} ms`
          );
        } else {
          notify.error(
            t('knowledgeManagement.llmProviders.testFailedTitle', 'Connexion échouée'),
            result.error ?? ''
          );
        }
      } catch (error) {
        notify.error(
          t('knowledgeManagement.llmProviders.testFailedTitle', 'Connexion échouée'),
          resolveApiError(error, t('knowledgeManagement.llmProviders.testError', 'Test impossible.'))
        );
      } finally {
        setTestingId(null);
      }
    },
    [organizationId, t]
  );

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">
            {t('knowledgeManagement.llmProviders.title', 'Fournisseurs IA')}
          </h3>
          <p className="text-xs text-muted-foreground">
            {t(
              'knowledgeManagement.llmProviders.subtitle',
              'Déclarez vos accès aux moteurs IA (clé, point de terminaison), puis affectez-les aux services ci-dessus.'
            )}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={startCreate}
          disabled={loading || !!draft || providerTypes.length === 0}
        >
          {t('knowledgeManagement.llmProviders.add', 'Ajouter un fournisseur')}
        </Button>
      </div>

      {loadError ? (
        <p className="text-sm text-destructive">{loadError}</p>
      ) : null}

      {loading ? (
        <p className="text-sm text-muted-foreground">
          {t('knowledgeManagement.llmProviders.loading', 'Chargement des fournisseurs...')}
        </p>
      ) : (
        <div className="space-y-2">
          {providers.length === 0 && !draft ? (
            <p className="text-sm text-muted-foreground">
              {t(
                'knowledgeManagement.llmProviders.empty',
                'Aucun fournisseur configuré. Les services IA utilisent les clés définies au niveau du serveur.'
              )}
            </p>
          ) : null}

          {providers.map((provider) => (
            <div
              key={provider.id}
              className="grid grid-cols-12 gap-2 items-center rounded-md border border-border px-3 py-2"
            >
              <div className="col-span-3 text-sm font-medium">
                {provider.label}
                {!provider.is_active ? (
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({t('knowledgeManagement.llmProviders.inactive', 'inactif')})
                  </span>
                ) : null}
              </div>
              <div className="col-span-2 text-xs text-muted-foreground">
                {typesByName.get(provider.provider_type)?.label ?? provider.provider_type}
              </div>
              <div className="col-span-3 truncate text-xs text-muted-foreground">
                {provider.base_url ?? t('knowledgeManagement.llmProviders.defaultEndpoint', 'Point de terminaison par défaut')}
              </div>
              <div className="col-span-2 text-xs text-muted-foreground">
                {provider.has_api_key
                  ? `•••• ${provider.api_key_last4 ?? ''}`
                  : t('knowledgeManagement.llmProviders.envKey', 'Clé serveur')}
              </div>
              <div className="col-span-2 flex justify-end gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => void handleTest(provider)}
                  disabled={testingId === provider.id}
                >
                  {testingId === provider.id
                    ? t('knowledgeManagement.llmProviders.testing', 'Test...')
                    : t('knowledgeManagement.llmProviders.test', 'Tester')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => startEdit(provider)}>
                  {t('actions.edit', 'Modifier')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => void handleDelete(provider)}>
                  {t('actions.delete', 'Supprimer')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {draft ? (
        <div className="space-y-3 rounded-md border border-border bg-background p-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">
                {t('knowledgeManagement.llmProviders.type', 'Type de fournisseur')}
              </span>
              <select
                className={inputClass}
                value={draft.provider_type}
                disabled={!!draft.id}
                onChange={(event) => {
                  const providerType = event.target.value;
                  const descriptor = typesByName.get(providerType);
                  setDraft((prev) =>
                    prev
                      ? {
                          ...prev,
                          provider_type: providerType,
                          base_url: descriptor?.default_base_url ?? '',
                        }
                      : prev
                  );
                }}
              >
                {providerTypes.map((type) => (
                  <option key={type.provider_type} value={type.provider_type}>
                    {type.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">
                {t('knowledgeManagement.llmProviders.label', 'Nom')}
              </span>
              <input
                className={inputClass}
                value={draft.label}
                placeholder={t('knowledgeManagement.llmProviders.labelPlaceholder', 'ex. Claude production')}
                onChange={(event) =>
                  setDraft((prev) => (prev ? { ...prev, label: event.target.value } : prev))
                }
              />
            </label>

            <label className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">
                {t('knowledgeManagement.llmProviders.baseUrl', 'Point de terminaison')}
                {selectedType?.requires_base_url ? ' *' : ''}
              </span>
              <input
                className={inputClass}
                value={draft.base_url}
                placeholder={selectedType?.default_base_url ?? 'https://...'}
                onChange={(event) =>
                  setDraft((prev) => (prev ? { ...prev, base_url: event.target.value } : prev))
                }
              />
            </label>

            <label className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">
                {t('knowledgeManagement.llmProviders.apiKey', 'Clé API')}
                {keyRequired ? ' *' : ''}
              </span>
              <input
                className={inputClass}
                type="password"
                autoComplete="new-password"
                value={draft.api_key}
                required={keyRequired}
                placeholder={
                  draft.id
                    ? t('knowledgeManagement.llmProviders.apiKeyUnchanged', 'Laisser vide pour conserver la clé actuelle')
                    : selectedType?.uses_workspace_identity
                      ? t(
                          'knowledgeManagement.llmProviders.apiKeyWorkspaceIdentity',
                          "Laisser vide pour utiliser l'identité de l'application Databricks"
                        )
                    : isSuperAdmin
                      ? t(
                          'knowledgeManagement.llmProviders.apiKeyPlaceholder',
                          'Laisser vide pour utiliser la clé serveur'
                        )
                      : t(
                          'knowledgeManagement.llmProviders.apiKeyRequiredPlaceholder',
                          'Clé API de votre organisation'
                        )
                }
                onChange={(event) =>
                  setDraft((prev) => (prev ? { ...prev, api_key: event.target.value } : prev))
                }
              />
            </label>

            {needsAzureFields ? (
              <>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">
                    {t('knowledgeManagement.llmProviders.apiVersion', 'Version d’API')}
                  </span>
                  <input
                    className={inputClass}
                    value={draft.api_version}
                    placeholder="2024-10-21"
                    onChange={(event) =>
                      setDraft((prev) => (prev ? { ...prev, api_version: event.target.value } : prev))
                    }
                  />
                </label>

                <label className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground">
                    {t('knowledgeManagement.llmProviders.deploymentMap', 'Correspondance modèle → déploiement')}
                  </span>
                  <textarea
                    className={`${inputClass} font-mono`}
                    rows={3}
                    value={draft.deployment_map}
                    placeholder={'{\n  "gpt-4o": "mon-deploiement"\n}'}
                    onChange={(event) =>
                      setDraft((prev) => (prev ? { ...prev, deployment_map: event.target.value } : prev))
                    }
                  />
                </label>
              </>
            ) : null}
          </div>

          {selectedType && !selectedType.supports_embeddings ? (
            <p className="text-xs text-muted-foreground">
              {t(
                'knowledgeManagement.llmProviders.noEmbeddings',
                "Ce fournisseur ne propose pas d'embeddings : l'indexation documentaire continuera d'utiliser OpenAI."
              )}
            </p>
          ) : null}

          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setDraft(null)} disabled={saving}>
              {t('actions.cancel', 'Annuler')}
            </Button>
            <Button size="sm" onClick={() => void handleSave()} disabled={saving}>
              {saving ? t('actions.saving', 'Sauvegarde...') : t('actions.save', 'Enregistrer')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Prefer the API's `detail` (it names bound services on a 409) over a generic message. */
function resolveApiError(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export default LLMProvidersCard;
